import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const SettingsTab = () => {
  // --- Ustawienia faktur: blokada faktury na firmę w POS ---
  const [blockFakturaFirma, setBlockFakturaFirma] = useState(false);
  const [invoiceSettingsLoading, setInvoiceSettingsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get('/admin/invoice-settings')
      .then((res) => { if (mounted) setBlockFakturaFirma(!!res?.data?.data?.block_faktura_firma); })
      .catch(() => { /* domyślnie odblokowane */ });
    return () => { mounted = false; };
  }, []);

  const saveInvoiceSettings = async (nextValue) => {
    setInvoiceSettingsLoading(true);
    const previous = blockFakturaFirma;
    setBlockFakturaFirma(nextValue); // optymistycznie
    try {
      await api.post('/admin/invoice-settings', { block_faktura_firma: nextValue });
      toast.success(nextValue
        ? 'Faktura na firmę zablokowana — ukryta w POS'
        : 'Faktura na firmę odblokowana');
    } catch (err) {
      setBlockFakturaFirma(previous); // rollback przy błędzie
      toast.error('Nie udało się zapisać ustawienia');
    } finally {
      setInvoiceSettingsLoading(false);
    }
  };

  const [systemSettings, setSystemSettings] = useState({
    company_name: '', tax_id: '', company_address: '', default_vat_rate: '23',
    currency: 'PLN', language: 'pl', auto_print_receipts: false,
    require_customer_info: false, enable_inventory_tracking: false, enable_loyalty_program: false
  });
  const [printerSettings, setPrinterSettings] = useState({
    printer_type: 'thermal', paper_width: '80', printer_port: 'USB001', copies: 1
  });
  const [securitySettings, setSecuritySettings] = useState({
    min_password_length: 8, session_timeout: 480, max_login_attempts: 5,
    require_strong_passwords: false, enable_two_factor: false, log_all_actions: false
  });

  // Uwaga: poniższe sekcje nie mają jeszcze endpointów backendu.
  // Zamiast cichego no-op (mylącego "zapisano") informujemy jawnie,
  // że funkcja jest niedostępna. Działa realnie tylko przełącznik
  // "Zablokuj fakturę na firmę" (invoice-settings) powyżej.
  const featureUnavailable = () => {
    toast('Ta funkcja jest jeszcze niedostępna — nic nie zostało zmienione', { icon: 'ℹ️' });
  };

  const saveSystemSettings = () => {
    featureUnavailable();
  };

  const savePrinterSettings = () => {
    featureUnavailable();
  };

  const saveSecuritySettings = () => {
    featureUnavailable();
  };

  const testPrinter = () => {
    featureUnavailable();
  };

  const clearCache = () => {
    featureUnavailable();
  };

  const optimizeDatabase = () => {
    featureUnavailable();
  };

  const checkSystemHealth = () => {
    featureUnavailable();
  };

  const restartSystem = () => {
    if (window.confirm('Czy na pewno chcesz zrestartować system?')) {
      featureUnavailable();
    }
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Ustawienia faktur / sprzedaży */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          🧾 Faktury / sprzedaż
        </h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={blockFakturaFirma}
            disabled={invoiceSettingsLoading}
            onChange={(e) => saveInvoiceSettings(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              Zablokuj wystawianie faktury na firmę
            </span>
            <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              Ukrywa opcję „Faktura firma" w POS i blokuje jej wystawienie na serwerze.
              Paragon z NIP i faktura imienna pozostają dostępne.
            </span>
          </span>
        </label>
      </div>

      {/* Ustawienia systemu */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          ⚙️ Ustawienia systemu
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Nazwa firmy
            </label>
            <input
              type="text"
              value={systemSettings?.company_name || ''}
              onChange={(e) => setSystemSettings({...systemSettings, company_name: e.target.value})}
              placeholder="Nazwa firmy"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              NIP
            </label>
            <input
              type="text"
              value={systemSettings?.tax_id || ''}
              onChange={(e) => setSystemSettings({...systemSettings, tax_id: e.target.value})}
              placeholder="NIP"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Adres firmy
            </label>
            <textarea
              value={systemSettings?.company_address || ''}
              onChange={(e) => setSystemSettings({...systemSettings, company_address: e.target.value})}
              placeholder="Adres firmy"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                minHeight: '60px',
                resize: 'vertical'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Domyślna stawka VAT (%)
            </label>
            <select
              value={systemSettings?.default_vat_rate || '23'}
              onChange={(e) => setSystemSettings({...systemSettings, default_vat_rate: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="0">0%</option>
              <option value="5">5%</option>
              <option value="8">8%</option>
              <option value="23">23%</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Waluta
            </label>
            <select
              value={systemSettings?.currency || 'PLN'}
              onChange={(e) => setSystemSettings({...systemSettings, currency: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="PLN">PLN (zł)</option>
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Język systemu
            </label>
            <select
              value={systemSettings?.language || 'pl'}
              onChange={(e) => setSystemSettings({...systemSettings, language: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="pl">Polski</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={systemSettings?.auto_print_receipts || false}
              onChange={(e) => setSystemSettings({...systemSettings, auto_print_receipts: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Automatyczne drukowanie paragonów</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={systemSettings?.require_customer_info || false}
              onChange={(e) => setSystemSettings({...systemSettings, require_customer_info: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Wymagaj danych klienta przy sprzedaży</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={systemSettings?.enable_inventory_tracking || false}
              onChange={(e) => setSystemSettings({...systemSettings, enable_inventory_tracking: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Włącz śledzenie stanów magazynowych</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={systemSettings?.enable_loyalty_program || false}
              onChange={(e) => setSystemSettings({...systemSettings, enable_loyalty_program: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Włącz program lojalnościowy</span>
          </label>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={saveSystemSettings}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              color: 'var(--info)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            💾 Zapisz ustawienia systemu
          </button>
        </div>
      </div>

      {/* Ustawienia drukarki */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          🖨️ Ustawienia drukarki
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Typ drukarki
            </label>
            <select
              value={printerSettings?.printer_type || 'thermal'}
              onChange={(e) => setPrinterSettings({...printerSettings, printer_type: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="thermal">Drukarka termiczna</option>
              <option value="inkjet">Drukarka atramentowa</option>
              <option value="laser">Drukarka laserowa</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Szerokość papieru (mm)
            </label>
            <select
              value={printerSettings?.paper_width || '80'}
              onChange={(e) => setPrinterSettings({...printerSettings, paper_width: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="58">58mm</option>
              <option value="80">80mm</option>
              <option value="210">A4 (210mm)</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Port drukarki
            </label>
            <input
              type="text"
              value={printerSettings?.printer_port || 'USB001'}
              onChange={(e) => setPrinterSettings({...printerSettings, printer_port: e.target.value})}
              placeholder="USB001"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Liczba kopii
            </label>
            <input
              type="number"
              value={printerSettings?.copies || 1}
              onChange={(e) => setPrinterSettings({...printerSettings, copies: parseInt(e.target.value) || 1})}
              min="1"
              max="5"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={testPrinter}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--warning)',
              color: 'var(--warning)',
              fontSize: '12px'
            }}
          >
            🧪 Test drukarki
          </button>
          <button
            onClick={savePrinterSettings}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              color: 'var(--info)',
              fontSize: '12px'
            }}
          >
            💾 Zapisz ustawienia
          </button>
        </div>
      </div>

      {/* Ustawienia bezpieczeństwa */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          🔒 Ustawienia bezpieczeństwa
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Minimalna długość hasła
            </label>
            <input
              type="number"
              value={securitySettings?.min_password_length || 8}
              onChange={(e) => setSecuritySettings({...securitySettings, min_password_length: parseInt(e.target.value) || 8})}
              min="6"
              max="20"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Czas sesji (minuty)
            </label>
            <input
              type="number"
              value={securitySettings?.session_timeout || 480}
              onChange={(e) => setSecuritySettings({...securitySettings, session_timeout: parseInt(e.target.value) || 480})}
              min="30"
              max="1440"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
              Maksymalna liczba prób logowania
            </label>
            <input
              type="number"
              value={securitySettings?.max_login_attempts || 5}
              onChange={(e) => setSecuritySettings({...securitySettings, max_login_attempts: parseInt(e.target.value) || 5})}
              min="3"
              max="10"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={securitySettings?.require_strong_passwords || false}
              onChange={(e) => setSecuritySettings({...securitySettings, require_strong_passwords: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Wymagaj silnych haseł</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={securitySettings?.enable_two_factor || false}
              onChange={(e) => setSecuritySettings({...securitySettings, enable_two_factor: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Włącz uwierzytelnianie dwuskładnikowe</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={securitySettings?.log_all_actions || false}
              onChange={(e) => setSecuritySettings({...securitySettings, log_all_actions: e.target.checked})}
            />
            <span style={{ fontSize: '0.875rem' }}>Loguj wszystkie akcje użytkowników</span>
          </label>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={saveSecuritySettings}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--danger)',
              color: 'var(--danger)',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            🔒 Zapisz ustawienia bezpieczeństwa
          </button>
        </div>
      </div>

      {/* Akcje systemowe */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
          🔧 Akcje systemowe
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <button
            onClick={clearCache}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--warning)',
              color: 'var(--warning)',
              fontSize: '12px',
              fontWeight: '500',
              textAlign: 'center'
            }}
          >
            🧹 Wyczyść cache
          </button>

          <button
            onClick={optimizeDatabase}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              color: 'var(--info)',
              fontSize: '12px',
              fontWeight: '500',
              textAlign: 'center'
            }}
          >
            ⚡ Optymalizuj bazę danych
          </button>

          <button
            onClick={checkSystemHealth}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--success)',
              color: 'var(--success)',
              fontSize: '12px',
              fontWeight: '500',
              textAlign: 'center'
            }}
          >
            🩺 Sprawdź stan systemu
          </button>

          <button
            onClick={restartSystem}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--danger)',
              color: 'var(--danger)',
              fontSize: '12px',
              fontWeight: '500',
              textAlign: 'center'
            }}
          >
            🔄 Restart systemu
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
